import { useCallback, useEffect, useRef, useState } from "react";
import type { PlaybackClock } from "@/lib/playback-clock";
import { postPlaybackLease } from "@/services/api/playback-lease";

/** Used until the server says otherwise (it answers `renewSec`, 15 s). */
const DEFAULT_RENEW_MS = 15_000;

/**
 * H3 measure 2: one video at a time per student account. Pressing play here takes the playback
 * lease over (the other device's player pauses at its next renewal); while playing, the lease is
 * renewed. When a renewal says another device is playing, this player pauses and `blocked` turns
 * on ("Playing on another device" + "Play here"). Pass `null` to switch it off (staff, no video).
 * A failed request (network) never stops playback.
 */
export function usePlaybackLease(clock: PlaybackClock | null): {
  blocked: boolean;
  playHere: () => void;
} {
  const [blocked, setBlocked] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const renewMsRef = useRef(DEFAULT_RENEW_MS);
  const clockRef = useRef(clock);
  clockRef.current = clock;

  const stopRenewing = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  const claim = useCallback(
    async (takeOver: boolean) => {
      let answer;
      try {
        answer = await postPlaybackLease(takeOver);
      } catch {
        return;
      }
      if (answer.playing) {
        renewMsRef.current = Math.max(1, answer.renewSec) * 1000;
        setBlocked(false);
        return;
      }
      stopRenewing();
      clockRef.current?.pause?.();
      setBlocked(true);
    },
    [stopRenewing],
  );

  const startRenewing = useCallback(() => {
    stopRenewing();
    timerRef.current = setInterval(() => void claim(false), renewMsRef.current);
  }, [claim, stopRenewing]);

  useEffect(() => {
    setBlocked(false);
    if (!clock) return;
    const unsubscribe = clock.subscribe({
      onTime: () => undefined,
      onEnded: () => undefined,
      onPlay: () => {
        void claim(true);
        startRenewing();
      },
      onPause: stopRenewing,
    });
    return () => {
      unsubscribe();
      stopRenewing();
    };
  }, [claim, clock, startRenewing, stopRenewing]);

  const playHere = useCallback(() => {
    setBlocked(false);
    // The play event claims the lease (and starts the renewals).
    clockRef.current?.play?.();
  }, []);

  return { blocked, playHere };
}
